import { UnorderedList, ListItem, IconButton } from '@carbon/react';
import { SubtractAlt } from '@carbon/react/icons';
import { formatSource } from './utils';
import { LinkOrText } from '../link-or-text';
import type { CurrentSelectionsProps, EvalUnifiedResult } from './types';
import style from './current-selections.module.scss';

/** Placeholder shown for an empty field or unfilled selection slot */
const PLACEHOLDER = '--';

interface SelectionBlockProps {
  /** Block label — "Origin" (baseline) or "Target" (compared against) */
  label: string;
  /** The picked evaluation, or null when the slot is empty */
  evaluation: EvalUnifiedResult | null;
  /**
   * Remove handler for this block's evaluation. When provided (and the slot is
   * filled), a red remove button is shown next to the label.
   */
  onRemove?: (evaluation: EvalUnifiedResult) => void;
}

/**
 * One labeled selection block showing Package, Eval ID, and Source for a picked
 * evaluation, or `--` placeholders when the slot is empty. When `onRemove` is
 * provided and the slot is filled, a red remove button clears this selection.
 * @param {SelectionBlockProps} props - Block label, evaluation, and remove handler
 * @return {JSX.Element} The selection block
 */
const SelectionBlock = ({
  label,
  evaluation,
  onRemove,
}: SelectionBlockProps) => (
  <div className={style.block}>
    <div className={style.blockHeader}>
      <p className={style.blockLabel}>{label}</p>
      {evaluation && onRemove && (
        <IconButton
          className={style.removeButton}
          label={`Remove ${label}`}
          kind="ghost"
          size="sm"
          align="left"
          onClick={() => onRemove(evaluation)}
        >
          <SubtractAlt />
        </IconButton>
      )}
    </div>
    <UnorderedList>
      <ListItem>
        <span className={style.fieldLabel}>Package: </span>
        <LinkOrText href={evaluation?.package?.href} className={style.link}>
          {evaluation?.package?.name ?? PLACEHOLDER}
        </LinkOrText>
      </ListItem>
      <ListItem>
        <span className={style.fieldLabel}>Eval ID: </span>
        <LinkOrText href={evaluation?.href} className={style.link}>
          {evaluation ? evaluation.evalId : PLACEHOLDER}
        </LinkOrText>
      </ListItem>
      <ListItem>
        <span className={style.fieldLabel}>Source: </span>
        <span>
          {evaluation ? formatSource(evaluation.source) : PLACEHOLDER}
        </span>
      </ListItem>
    </UnorderedList>
  </div>
);

/**
 * Right-hand sidebar summarizing the current selection pair. The first picked
 * evaluation is the Origin (baseline); the second is the Target it is compared
 * against — order matters. Empty slots render `--`. Each filled slot shows a
 * red remove button (via `onRemove`) that clears it; deselection also stays
 * available through the table checkboxes.
 * @param {CurrentSelectionsProps} props - The selected evaluations (max 2) and remove handler
 * @return {JSX.Element} The current selections sidebar
 */
export const CurrentSelections = ({
  selections,
  onRemove,
}: CurrentSelectionsProps) => (
  <aside className={style.currentSelections} aria-label="Current Selections">
    <h2 className={style.heading}>Current Selections</h2>
    <SelectionBlock
      label="Origin"
      evaluation={selections[0] ?? null}
      onRemove={onRemove}
    />
    <SelectionBlock
      label="Target"
      evaluation={selections[1] ?? null}
      onRemove={onRemove}
    />
  </aside>
);
